import {
  Text,
  Stack,
  Group,
  Button,
  Textarea,
  Radio,
  FileInput,
  Paper,
} from '@mantine/core';
import {
  FaTrash,
  FaUpload,
  FaFileAlt,
  FaFilePdf,
  FaFileImage,
  FaVideo,
} from 'react-icons/fa';
import { JobQuestion } from '@worker/types/myJobs.type';
import VideoRecorderInput from '@components/elements/VideoRecorderInput/VideoRecorderInput';

const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_FILE_TYPES = '.pdf,.doc,.docx,.jpg,.jpeg,.png';

interface QuestionRendererProps {
  question: JobQuestion;
  response: string;
  file: File | null;
  videoPreviewUrl?: string;
  onResponseChange: (value: string) => void;
  onFileChange: (file: File | null) => void;
  onError: (message: string) => void;
}

/**
 * Get appropriate icon for file type
 */
const getFileIcon = (file: File) => {
  const { type } = file;
  if (type.startsWith('image/')) return <FaFileImage size={20} />;
  if (type === 'application/pdf') return <FaFilePdf size={20} />;
  if (type.startsWith('video/')) return <FaVideo size={20} />;
  return <FaFileAlt size={20} />;
};

/**
 * Format file size for display
 */
const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Component that renders the appropriate input for a job question based on its type
 */
function QuestionRenderer({
  question,
  response,
  file,
  videoPreviewUrl,
  onResponseChange,
  onFileChange,
  onError,
}: QuestionRendererProps) {
  const handleFileInputChange = (newFile: File | null) => {
    if (newFile && newFile.size > MAX_FILE_SIZE_BYTES) {
      onError(`File exceeds ${MAX_FILE_SIZE_MB}MB limit`);
      return;
    }
    onFileChange(newFile);
  };

  switch (question.type) {
    case 'open-field':
      return (
        <Textarea
          label={question.text}
          placeholder="Type your answer here..."
          value={response}
          onChange={(e) => onResponseChange(e.currentTarget.value)}
          minRows={4}
          autosize
          maxRows={8}
          maxLength={600}
          description={`${response.length}/600 characters`}
        />
      );

    case 'multiple-choice':
      return (
        <Radio.Group
          label={question.text}
          value={response}
          onChange={onResponseChange}
        >
          <Stack gap="xs" mt="xs">
            {question.choices?.map((choice) => (
              <Radio key={choice} value={choice} label={choice} />
            ))}
          </Stack>
        </Radio.Group>
      );

    case 'attach-file':
      return (
        <Stack gap="md">
          <Text fw={500}>{question.text}</Text>
          <Text size="sm" c="dimmed">
            Accepted formats: PDF, DOC, DOCX, JPG, PNG (max {MAX_FILE_SIZE_MB}
            MB)
          </Text>

          {file ? (
            <Paper p="md" withBorder>
              <Group justify="space-between">
                <Group gap="sm">
                  {getFileIcon(file)}
                  <Stack gap={0}>
                    <Text size="sm" fw={500} lineClamp={1}>
                      {file.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatFileSize(file.size)}
                    </Text>
                  </Stack>
                </Group>
                <Button
                  variant="subtle"
                  color="red"
                  size="sm"
                  leftSection={<FaTrash size={14} />}
                  onClick={() => onFileChange(null)}
                >
                  Remove
                </Button>
              </Group>
            </Paper>
          ) : (
            <FileInput
              placeholder="Click to upload file"
              leftSection={<FaUpload size={14} />}
              accept={ACCEPTED_FILE_TYPES}
              onChange={handleFileInputChange}
            />
          )}
        </Stack>
      );

    case 'video':
      return (
        <VideoRecorderInput
          questionText={question.text}
          uploadedVideo={file}
          videoPreviewUrl={videoPreviewUrl}
          onFileChange={onFileChange}
          onError={onError}
        />
      );

    default:
      return (
        <Stack gap="md">
          <Text fw={500}>{question.text}</Text>
          <Text c="dimmed" size="sm">
            This question type is not supported.
          </Text>
        </Stack>
      );
  }
}

export default QuestionRenderer;
